


// src/App.js
import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { DemoProvider } from "./context/DemoContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import NotificationListener from "./components/notifications/NotificationListener";
import "./App.css";

// Routes that need immediate loading (critical path)
import AdminRoute from "./components/auth/AdminRoute";
import EmployeeRoute from "./components/employee/EmployeeRoute";
import Login from "./screens/auth/Login";
import WelcomePage from "./screens/public/WelcomePage";

const Onboarding = lazy(() => import("./screens/auth/Onboarding"));


// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600 dark:border-indigo-400"></div>
  </div>
);

// Lazy load public pages
const PricePage = lazy(() => import("./screens/public/PricePage"));
const Unauthorized = lazy(() => import("./screens/auth/Unauthorized"));
const NotFoundPage = lazy(() => import("./screens/public/NotFoundPage"));
const PublicRequestForm = lazy(() => import("./components/common/PublicRequestForm"));
const PublicRequestPage = lazy(() => import("./screens/public/PublicRequestPage"));
const Walkthrough = lazy(() => import("./screens/public/Walkthrough"));
const ImportGuide = lazy(() => import("./screens/public/ImportGuide"));
const DemoPage = lazy(() => import("./screens/public/DemoPage"));
const DemoDashboard = lazy(() => import("./screens/public/DemoDashboard"));
const PrivacyPolicy = lazy(() => import("./screens/public/PrivacyPolicy"));
const PrivacyPolicyPT = lazy(() => import("./screens/public/PrivacyPolicyPT"));
const PrivacyPolicyES = lazy(() => import("./screens/public/PrivacyPolicyES"));
const TermsOfService = lazy(() => import("./screens/public/TermsOfService"));
const StatusPage = lazy(() => import("./screens/public/StatusPage"));
const SupportPT = lazy(() => import("./screens/public/SupportPT"));
const SupportES = lazy(() => import("./screens/public/SupportES"));
const CinematicTraining = lazy(() => import("./screens/public/CinematicTraining"));
const DemoLayout = lazy(() => import("./screens/public/DemoLayout"));

// Lazy load customer-facing pages
const AcceptInvoice = lazy(() => import("./screens/documents/AcceptInvoice"));
const AcceptBooking = lazy(() => import("./screens/documents/AcceptBooking"));
const AcceptEstimate = lazy(() => import("./screens/documents/AcceptEstimate"));
const AcceptContract = lazy(() => import("./screens/documents/AcceptContract"));
const AcceptQuote = lazy(() => import("./screens/documents/AcceptQuote"));
const AcceptProposal = lazy(() => import("./screens/documents/AcceptProposal"));
const AcceptBillOfSale = lazy(() => import("./screens/documents/AcceptBillOfSale"));
const PayInvoice = lazy(() => import("./screens/documents/PayInvoice"));
const PayDeposit = lazy(() => import("./screens/documents/PayDeposit"));
const BusinessProfilePage = lazy(() => import("./screens/public/BusinessProfilePage"));
const ReviewSubmissionPage = lazy(() => import("./screens/public/ReviewSubmissionPage"));

// Lazy load admin pages
const Dashboard = lazy(() => import("./screens/admin/Dashboard"));
const Insights = lazy(() => import("./screens/admin/Insights"));
const Support = lazy(() => import("./screens/settings/Support"));
const Tasks = lazy(() => import("./screens/admin/Tasks"));
const Employees = lazy(() => import("./screens/admin/Employees"));
const CustomersPage = lazy(() => import("./screens/admin/CustomersPage"));
const Messaging = lazy(() => import("./components/messaging/Messaging"));
const CustomerMessaging = lazy(() => import("./components/messaging/CustomerMessaging"));
const Schedule = lazy(() => import("./components/schedule/Schedule"));
const AdminProfile = lazy(() => import("./screens/settings/AdminProfile"));
const TeamManagement = lazy(() => import("./components/settings/TeamManagement"));
const InvoiceEstimateSettings = lazy(() => import("./components/settings/Settings"));
const BrandingSettings = lazy(() => import("./components/settings/BrandingSettings"));
const CreateInvoice = lazy(() => import("./components/documents/CreateInvoice"));
const InvoiceSection = lazy(() => import("./components/documents/InvoiceSection"));
const InvoiceDetails = lazy(() => import("./components/documents/InvoiceDetails"));
const PaymentOptions = lazy(() => import("./screens/settings/PaymentOptions"));
const Bookings = lazy(() => import("./components/booking/Booking"));
const BookingCreate = lazy(() => import("./components/booking/BookingCreate"));
const BookingDetails = lazy(() => import("./components/booking/BookingDetails"));
const RecurringJobBookings = lazy(() => import("./components/booking/RecurringJobBookings"));
const BulkScheduleBookings = lazy(() => import("./components/booking/BulkScheduleBookings"));
const Estimates = lazy(() => import("./components/documents/Estimates"));
const EstimateCreate = lazy(() => import("./components/documents/EstimatesCreate"));
const EstimateDetails = lazy(() => import("./components/documents/EstimateDetails"));
const Requests = lazy(() => import("./components/documents/Request"));
const RequestsCreate = lazy(() => import("./components/documents/RequestsCreate"));
const RequestDetails = lazy(() => import("./components/documents/RequestDetails"));
const Contracts = lazy(() => import("./components/documents/Contracts"));
const ContractCreate = lazy(() => import("./components/documents/ContractCreate"));
const ContractDetails = lazy(() => import("./components/documents/ContractDetails"));
const Quotes = lazy(() => import("./components/documents/Quotes"));
const QuoteCreate = lazy(() => import("./components/documents/QuoteCreate"));
const QuoteDetails = lazy(() => import("./components/documents/QuoteDetails"));
const Proposals = lazy(() => import("./components/documents/Proposals"));
const ProposalCreate = lazy(() => import("./components/documents/ProposalCreate"));
const ProposalDetails = lazy(() => import("./components/documents/ProposalDetails"));
const BillsOfSale = lazy(() => import("./components/documents/BillsOfSale"));
const BillOfSaleCreate = lazy(() => import("./components/documents/BillOfSaleCreate"));
const BillOfSaleDetails = lazy(() => import("./components/documents/BillOfSaleDetails"));
const SignedDocuments = lazy(() => import("./screens/admin/SignedDocuments"));
const Products = lazy(() => import("./screens/admin/Products"));
const Services = lazy(() => import("./screens/admin/Services"));
const Suppliers = lazy(() => import("./screens/admin/Suppliers"));
const SupplierDetail = lazy(() => import("./screens/admin/SupplierDetail"));
const Expenses = lazy(() => import("./screens/data/Expenses"));
const Transactions = lazy(() => import("./screens/data/Transaction"));
const Timesheet = lazy(() => import("./screens/data/TimeSheet"));
const Reports = lazy(() => import("./screens/data/Reports"));
const Subscriptions = lazy(() => import("./screens/data/Subscription"));
const Notifications = lazy(() => import("./screens/settings/Notifications"));
const Automations = lazy(() => import("./screens/settings/Automations"));
const Security = lazy(() => import("./screens/settings/Security"));
const RecurringJobs = lazy(() => import("./screens/settings/RecurringJobs"));
const BulkSchedule = lazy(() => import("./screens/settings/BulkSchedule"));
const RecurringJobDisplay = lazy(() => import("./screens/admin/RecurringJobDisplay"));
const GettingStartedWorkflow = lazy(() => import("./components/common/GettingStartedWorkflow"));
const LocationTracking = lazy(() => import("./screens/settings/LocationTracking"));
const DataExport = lazy(() => import("./screens/settings/DataExport"));
const AvailabilitySettings = lazy(() => import("./screens/settings/AvailabilitySettings"));
const MyWebsite = lazy(() => import("./screens/admin/MyWebsite"));
const Marketing = lazy(() => import("./screens/admin/Marketing"));
const Payroll = lazy(() => import("./screens/admin/Payroll"));
const NotificationPreferences = lazy(() => import("./screens/settings/NotificationPreferences"));
const Integrations = lazy(() => import("./screens/settings/Integrations"));
const ShiftSchedule = lazy(() => import("./screens/admin/ShiftSchedule"));

// Lazy load employee pages
const EmployeeDashboard = lazy(() => import("./screens/employee/EmployeeDashboard"));
const EmployeeMessages = lazy(() => import("./screens/employee/EmployeeMessages"));
const EmployeeCompletedTasks = lazy(() => import("./screens/employee/EmployeeCompletedTasks"));
const EmployeeProfile = lazy(() => import("./screens/employee/EmployeeProfile"));
const EmployeeCalendar = lazy(() => import("./screens/employee/EmployeeCalendar"));
const EmployeeTasks = lazy(() => import("./screens/employee/EmployeeTasks"));
const EmployeeExpenses = lazy(() => import("./screens/employee/EmployeeExpenses"));

// Lazy load platform (super admin) pages
const SuperAdminRoute = lazy(() => import("./components/auth/SuperAdminRoute"));
const PlatformSupportDashboard = lazy(() => import("./screens/platform/PlatformSupportDashboard"));

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <DemoProvider>
          <Router>
            <ThemeProvider>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
            <NotificationListener />
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
            {/* ── Public ─────────────────────────────────── */}
            <Route path="/" element={<WelcomePage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/pricing" element={<PricePage />} />
              <Route path="/walkthrough" element={<Walkthrough />} />
              <Route path="/training" element={<CinematicTraining />} />
              <Route path="/import-guide" element={<ImportGuide />} />
              <Route path="/demo" element={<DemoPage />} />
              <Route path="/demo/dashboard" element={<DemoDashboard />} />
              <Route path="/demo/layout" element={<DemoLayout />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/privacy-policy/pt" element={<PrivacyPolicyPT />} />
              <Route path="/privacy-policy/es" element={<PrivacyPolicyES />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/status" element={<StatusPage />} />
              <Route path="/support" element={<Support />} />
              <Route path="/support/pt" element={<SupportPT />} />
              <Route path="/support/es" element={<SupportES />} />
              <Route path="/public-request" element={<PublicRequestForm />} />
              <Route path="/request/:businessId" element={<PublicRequestPage />} />

            {/* Public business profile pages */}
            <Route path="/b/:slug" element={<BusinessProfilePage />} />
            <Route path="/b/:slug/:pageSlug" element={<BusinessProfilePage />} />
            <Route path="/review/:businessId/:bookingId" element={<ReviewSubmissionPage />} />

            {/* accept via link in email */}
            <Route path="/accept-booking" element={<AcceptBooking />} />
            <Route path="/accept-invoice" element={<AcceptInvoice />} />
            {/* Secure publicShares route */}
            <Route path="/invoice/:shareId" element={<AcceptInvoice />} />
            <Route path="/share/:shareId" element={<AcceptInvoice />} />
            {/* Estimate routes */}
            <Route path="/accept-estimate/:estimateId" element={<AcceptEstimate />} />
            <Route path="/estimate/:shareId" element={<AcceptEstimate />} />
            {/* Contract routes */}
            <Route path="/contract/:shareId" element={<AcceptContract />} />
            {/* Quote routes */}
            <Route path="/quote/:shareId" element={<AcceptQuote />} />
            {/* Proposal routes */}
            <Route path="/proposal/:shareId" element={<AcceptProposal />} />
            {/* Bill of Sale routes */}
            <Route path="/bill-of-sale/:shareId" element={<AcceptBillOfSale />} />

            {/* Payment routes */}
            <Route path="/pay" element={<PayInvoice />} />
            <Route path="/pay-deposit" element={<PayDeposit />} />

          {/* ── Employee-protected ───────────────────────── */}
          <Route path="/employee" element={<EmployeeRoute />}>
            <Route index element={<EmployeeDashboard />} />
            <Route path="dashboard" element={<EmployeeDashboard />} />
            <Route path="tasks" element={<EmployeeTasks />} />
            <Route path="messages" element={<EmployeeMessages />} />
            <Route path="completed-tasks" element={<EmployeeCompletedTasks />} />
            <Route path="profile" element={<EmployeeProfile />} />
            <Route path="calendar" element={<EmployeeCalendar />} />
            <Route path="expenses" element={<EmployeeExpenses />} />
          </Route>

          {/* ── Admin-protected ───────────────────────── */}
          <Route path="/admin" element={<AdminRoute />}>
            <Route index element={<Dashboard />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="insights" element={<Insights />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="employees" element={<Employees />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="messaging" element={<Messaging />} />
            <Route path="customer-messages" element={<CustomerMessaging />} />
            <Route path="schedule" element={<Schedule />} />
            <Route path="admin-profile" element={<AdminProfile />} />
            <Route path="team-management" element={<TeamManagement />} />
            <Route path="settings" element={<InvoiceEstimateSettings />} />
            <Route path="branding" element={<BrandingSettings />} />
            <Route path="create-invoice" element={<CreateInvoice />} />
            <Route path="invoices" element={<InvoiceSection />} />
            <Route path="invoice/:id" element={<InvoiceDetails />} />
            <Route path="payments" element={<PaymentOptions />} />
            <Route path="bookings" element={<Bookings />} />
            <Route path="booking-page" element={<BookingCreate />} />
            <Route path="bookings/:bookingId" element={<BookingCreate />} />
            <Route path="booking/:id" element={<BookingDetails />} />
            <Route path="recurring-bookings/:recurringJobId" element={<RecurringJobBookings />} />
            <Route path="bulk-schedule-bookings/:bulkScheduleId" element={<BulkScheduleBookings />} />
            <Route path="estimates" element={<Estimates />} />
            <Route path="estimates-create" element={<EstimateCreate />} />
            <Route path="estimates/:id" element={<EstimateDetails />} />
            <Route path="contracts" element={<Contracts />} />
            <Route path="contracts-create" element={<ContractCreate />} />
            <Route path="contracts/:id" element={<ContractDetails />} />
            <Route path="quotes" element={<Quotes />} />
            <Route path="quotes-create" element={<QuoteCreate />} />
            <Route path="quotes/:id" element={<QuoteDetails />} />
            <Route path="proposals" element={<Proposals />} />
            <Route path="proposals-create" element={<ProposalCreate />} />
            <Route path="proposals/:id" element={<ProposalDetails />} />
            <Route path="bills-of-sale" element={<BillsOfSale />} />
            <Route path="bills-of-sale-create" element={<BillOfSaleCreate />} />
            <Route path="bills-of-sale/:id" element={<BillOfSaleDetails />} />
            <Route path="signed-documents" element={<SignedDocuments />} />
            <Route path="requests" element={<Requests />} />
            <Route path="requests-create" element={<RequestsCreate />} />
            <Route path="requests/:requestId" element={<RequestDetails />} />
            <Route path="produce" element={<Products />} />
            <Route path="services" element={<Services />} />
            <Route path="services/:id" element={<Services />} />
            <Route path="suppliers" element={<Suppliers />} />
            <Route path="suppliers/:id" element={<SupplierDetail />} />
            <Route path="expenses" element={<Expenses />} />
            <Route path="expenses/:id" element={<Expenses />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="transactions/:id" element={<Transactions />} />
            <Route path="timesheet" element={<Timesheet />} />
            <Route path="reports" element={<Reports />} />
            <Route path="reports/:id" element={<Reports />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="subscriptions/:id" element={<Subscriptions />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="notification-preferences" element={<NotificationPreferences />} />
            <Route path="automations" element={<Automations />} />
            <Route path="recurring-jobs" element={<RecurringJobs />} />
            <Route path="recurring-jobs/:jobId" element={<RecurringJobDisplay />} />
            <Route path="bulk-schedule" element={<BulkSchedule />} />
            <Route path="location-tracking" element={<LocationTracking />} />
            <Route path="security" element={<Security />} />
            <Route path="support" element={<Support />} />
            <Route path="getting-started" element={<GettingStartedWorkflow />} />
            <Route path="data-export" element={<DataExport />} />
            <Route path="availability" element={<AvailabilitySettings />} />
            <Route path="my-website" element={<MyWebsite />} />
            <Route path="marketing" element={<Marketing />} />
            <Route path="payroll" element={<Payroll />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="shift-schedule" element={<ShiftSchedule />} />
          </Route>

          {/* ── Platform (Super Admin) ─────────────────── */}
          <Route path="/platform" element={<SuperAdminRoute />}>
            <Route path="support" element={<PlatformSupportDashboard />} />
          </Route>

            {/* ── 404 ───────────────────────────────────── */}
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
            </ThemeProvider>
          </Router>
        </DemoProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
